import { storeAsync } from '@/app-module';

import { SubpropertyService } from '@modules/subproperty/subproperty-service';

import { i18n } from '@shared/i18n';
import LinkModel from '@shared/settings/link-model';
import { getSubpropertyRequirementStepsByPropertyType } from '@shared/factories/enums-factory';

import { get } from 'lodash';
import Errors from '@shared/error/errors';

const requirementsConfig = ({ paymentsEnabled, propertyId, subpropertyId }) => ({
  profile: {
    icon: 'icon-player',
    title: i18n('entities.property.requirementSteps.steps.profile.title'),
    link: new LinkModel(i18n('entities.property.requirementSteps.steps.profile.linkCaption'), null),
    onClick() {
      this.$router.push({
        name: 'editProfile'
      });
    },
  },
  categories: {
    icon: 'icon-folder',
    title: i18n('entities.subproperty.requirementSteps.steps.categories.title'),
    link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.categories.linkCaption'), null),
    onClick() {
      this.$router.push({
        name: 'programSettings',
        params: {
          tabName: 'categories'
        }
      });
    },
  },
  locations: {
    icon: 'icon-location',
    title: i18n('entities.subproperty.requirementSteps.steps.locations.title'),
    link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.locations.linkCaption'), null),
    onClick() {
      this.$router.push({
        name: 'programSettings',
        params: {
          tabName: 'locations'
        }
      });
    },
  },
  slug: {
    icon: 'icon-link',
    title: i18n('entities.subproperty.requirementSteps.steps.slug.title'),
    link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.slug.linkCaption'), null),
    onClick() {
      this.$router.push({ name: 'subpropertyCustomizeSettings' });
    },
  },
  ...!!paymentsEnabled && {
    setup: {
      icon: 'icon-settings',
      title: i18n('entities.subproperty.requirementSteps.steps.setup.title'),
      link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.setup.linkCaption'), null),
      onClick() {
        this.$router.push({ name: 'stripeConnectedAccountCreate' });
      },
    },
    people: {
      icon: 'icon-player',
      title: i18n('entities.subproperty.requirementSteps.steps.people.title'),
      link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.people.linkCaption'), null),
      onClick(connectedAccountId) {
        this.$router.push({
          name: 'stripePersonCreate',
          params: { connectedAccountId },
        });
      },
    },
    externalAccounts: {
      icon: 'icon-card',
      title: i18n('entities.subproperty.requirementSteps.steps.externalAccounts.title'),
      link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.externalAccounts.linkCaption'), null),
      onClick(connectedAccountId) {
        this.$router.push({
          name: 'stripeExternalAccountCreate',
          params: { connectedAccountId },
        });
      },
    },
    payouts: {
      icon: 'icon-calendar',
      title: i18n('entities.subproperty.requirementSteps.steps.payouts.title'),
      link: new LinkModel(i18n('entities.subproperty.requirementSteps.steps.payouts.linkCaption'), null),
      onClick() {
        this.$router.push({
          name: 'finantialSettings',
          params: {
            tabType: 'payoutSchedule',
            propertyId,
            subpropertyId,
          }
        });
      },
    },
  },
});

const areRequirementsFullfiled = (steps, allowedSteps = []) => {
  if (Object.keys(steps).length === 0) return true;
  
  const filteredSteps = Object.keys(steps)
  .filter(key => allowedSteps.length ? allowedSteps.includes(key) : true)
  .reduce((obj, key) => {
    obj[key] = steps[key];
    return obj;
  }, {});

  return !!Object.values(filteredSteps).length && Object.values(filteredSteps).every(({ done }) => done);
};

export default {
  namespaced: true,

  state: {
    loading: false,
    loadingPromise: null,
    loadingPromiseResolve: () => {},
    connectedAccountId: null,
    steps: {},
  },

  getters: {
    loading: state => state.loading,
    loadingPromise: state => state.loadingPromise,
    loadingPromiseResolve: state => state.loadingPromiseResolve,
    connectedAccountId: state => state.connectedAccountId,
    steps: state => state.steps,
    stepsArray: state => Object.values(state.steps),
    areRequirementsFullfiled: state => ({
      all: areRequirementsFullfiled(state.steps),
      finantial: areRequirementsFullfiled(state.steps, ['setup', 'people', 'externalAccounts', 'payouts']),
      academic: areRequirementsFullfiled(state.steps, ['categories', 'locations']),
    }),
  },

  mutations: {
    FETCH_STARTED(state) {
      state.loading = true;
      state.loadingPromise = new Promise(resolve => state.loadingPromiseResolve = resolve);
    },

    FETCH_SUCCESS(state, { connectedAccountId, steps }) {
      state.connectedAccountId = connectedAccountId;
      state.steps = steps;
      state.loading = false;
      state.loadingPromiseResolve();
      state.loadingPromise = null;
    },

    FETCH_ERROR(state) {
      state.connectedAccountId = null;
      state.steps = {};
      state.loading = false;
      state.loadingPromiseResolve();
      state.loadingPromise = null;
    },
  },

  actions: {
    async doFetch(
      { state, commit, rootGetters },
      { propertyId, subpropertyId, userId }
    ) {
      try {
        if (state.loadingPromise) return state.loadingPromise;

        commit('FETCH_STARTED');

        const response = await SubpropertyService.requirementSteps(propertyId, subpropertyId, userId);
        const { paymentsEnabled } = await storeAsync().getters['auth/manage/currentManage'].subproperty;

        const steps = Object.assign({}, requirementsConfig({ paymentsEnabled, propertyId, subpropertyId }));

        const propetyType = get(rootGetters['auth/manage/currentManage'], 'property.type', null);
        const requirementStepsByPropertyType = getSubpropertyRequirementStepsByPropertyType(propetyType) || [];

        response.steps.forEach(({ key, done, active }) => {
          if (!(key in steps) || !(requirementStepsByPropertyType.includes(key))) { 
            delete steps[key];
            return
          }

          steps[key].done = done;
          steps[key].active = active;

        });

        const { connectedAccountId } = response;

        commit('FETCH_SUCCESS', { steps, connectedAccountId });
      } catch (error) {
        Errors.handle(error);
        commit('FETCH_ERROR');
      }
    },
    
    doReset({ commit }) {
      commit('FETCH_ERROR');
    }
  },
};
